/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { CommonModule } from "@angular/common";
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
} from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  Subscription, // Import EMPTY
  take,
} from "rxjs";
import { DataService } from "../../../services/data.service";
import { DeliveryService } from "../../../services/delivery.service";
import { QPilotCloudApiService } from "../../../services/qpilot-cloud-api.service";
import { SiteSettingsService } from "../../../services/site-settings.service";
import { errorSnackBar, RemappedOmit, successSnackBar } from "../../../utils";
import { BarTagComponent } from "../../bar-tag/bar-tag.component";
import { QPilotProduct } from "../../types/product";
import { QPilotScheduledOrder } from "../../types/scheduled-order";
import { QPilotScheduledOrderItem } from "../../types/scheduled-order-item";
import { BundleProductListItemSelectedListComponent } from "../bundle-product-list-item-selected-list/bundle-product-list-item-selected-list.component";
import { BundleProductListComponent } from "../bundle-product-list/bundle-product-list.component";
import { BundleSizeComponent } from "../bundle-size/bundle-size.component";
import {
  Availability,
  BundleProductDto,
  BundleShippingFrequencyOptionsDto,
  GetBundleSettingsAggregatedDto,
  IBundle,
  IBundleItemRewardSetting,
} from "../bundle.interfaces";

@Component({
  selector: "lib-update-bundle",
  standalone: true,
  imports: [
    MatDialogModule,
    CommonModule,
    BundleProductListComponent,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
    BundleProductListItemSelectedListComponent,
    BundleSizeComponent,
    MatSelectModule,
    BarTagComponent,
    ReactiveFormsModule,
  ],
  templateUrl: "./update-bundle.component.html",
  styleUrl: "./update-bundle.component.scss",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UpdateBundleComponent implements OnInit, OnDestroy {
  @ViewChild(BundleProductListComponent)
  bundleProductList: BundleProductListComponent;
  // @Input() products: QPilotProduct[] = [];
  @Input() orderVerboseName: string;
  private forkJoinSubscription: Subscription; // Add subscription variable
  private getBundleByIdSubscription: Subscription;

  bundleForm: FormGroup;
  bundleSize: number = 4;
  selectedBundle: number = 0; // Default to 4 item bundle
  selectedFrequency: BundleShippingFrequencyOptionsDto | null = null;
  // @Output() selectedProductsChange = new EventEmitter<QPilotProduct[]>();
  selectedProducts: BundleProductDto[] = [];
  quantityToAdd = 1;
  bundle: IBundle;
  displayItems: QPilotScheduledOrderItem[] = [];
  autoshipSavePercentage: number | null;
  initialLoad: boolean = true;
  totalPrice: number = 0;
  rewardsSettings: IBundleItemRewardSetting[] = [];
  order: QPilotScheduledOrder;
  selectBundle(bundleSize: number) {
    this.selectedProducts = [];
    this.totalSavingsPercentage = 0;
    this.selectedBundle = bundleSize;
    this.bundleForm.patchValue({
      selectedBundle: bundleSize,
    });
  }
  private productsSubscription: Subscription;
  totalSavingsPercentage: number | null = null;
  totalSavings: number | null = null;
  isFreeShipping: boolean = false;
  bundleSettings: GetBundleSettingsAggregatedDto;
  isMobileView: boolean = false; // Add property to store the flag
  allProducts: QPilotProduct[] = [];
  constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Inject(MAT_DIALOG_DATA) public data: any, // Inject MAT_DIALOG_DATA
    private dialog: MatDialog,
    private dataService: DataService,
    protected siteSettingsService: SiteSettingsService,
    private qPilotCloudApiService: QPilotCloudApiService,
    private fb: FormBuilder,
    protected deliveryService: DeliveryService,
    private snackBar: MatSnackBar,
  ) {
    this.isMobileView = data?.isMobileView ?? false; // Initialize isMobileView from injected data
  }

  ngOnInit() {
    this.qPilotCloudApiService
      .getProducts()
      .subscribe((x) => (this.allProducts = x.items));

    this.selectedProducts = [];
    this.bundleForm = this.fb.group({
      selectedBundle: new FormControl(this.selectedBundle),
      selectedFrequency: new FormControl(""),
    });

    this.bundleForm.get("selectedFrequency")?.valueChanges.subscribe({
      next: () => {
        this.calculateRewards();
      },
      error: (error) => {
        console.error("Error calculating rewards:", error);
      },
    });

    this.orderVerboseName = this.siteSettingsService.orderVerboseName;

    this.dataService
      .getCurrentOrder()
      .pipe(take(1))
      .subscribe((order: QPilotScheduledOrder) => {
        this.order = order;
        if (this.order.bundleId) {
          this.getBundleByIdSubscription = this.qPilotCloudApiService
            .getBundleById(this.order.bundleId)
            .subscribe({
              next: (data) => {
                this.bundle = data;
                this.rewardsSettings = data.rewardsSettings;
                if (this.rewardsSettings && this.rewardsSettings.length > 0) {
                  this.selectBundle(this.bundle.maxItemCount!);
                }
                this.loadBundleSettings();
              },
              error: (error) => {
                console.error("Error fetching bundle:", error);
              },
            });
        }
      });
  }

  private calculateRewards(): void {
    if (this.bundle && this.bundle.siteId && this.bundle.id) {
      const selectedFrequency = this.bundleForm.get("selectedFrequency")
        ?.value as BundleShippingFrequencyOptionsDto | null;
      const productIds: number[] = [];
      this.selectedProducts.forEach((item) => {
        for (let i = 0; i < (item.quantity ?? 1); i++) {
          productIds.push(item.productId);
        }
      });
      const calculateBundleRewardsRequest = {
        productIds: productIds,
        frequencyOptionsId: selectedFrequency?.id,
      };

      this.qPilotCloudApiService
        .calculateBundleRewards(
          this.bundle.siteId,
          this.bundle.id,
          calculateBundleRewardsRequest,
        )
        .subscribe((result) => {
          if (result) {
            this.totalSavingsPercentage = result.totalSavingsPercentage;
            this.totalSavings = result.totalSavings;
            this.isFreeShipping = result.isFreeShipping;
          }
        });
    }
  }

  // Format the frequency option display text
  formatFrequencyOption(option: BundleShippingFrequencyOptionsDto): string {
    // Calculate total quantity of all selected products
    const itemCount = this.selectedProducts.reduce((total, product) => {
      return total + (product.quantity ?? 1);
    }, 0);

    const itemText = itemCount === 1 ? "item" : "items";
    const shipText = itemCount === 1 ? "ships" : "ship";
    const frequencyText =
      option.frequency === 1 ? option.type.slice(0, -1) : option.type;

    let displayText = `${itemCount} ${itemText} ${shipText} every ${option.frequency} ${frequencyText.toLowerCase()}`;

    if (option.discountPercentage && option.discountPercentage > 0) {
      displayText += ` (additional ${option.discountPercentage}% off)`;
    }

    return displayText;
  }

  private loadBundleSettings(): void {
    if (this.bundle && this.bundle.siteId && this.bundle.id) {
      this.qPilotCloudApiService
        .getBundleSettings(this.bundle.siteId, this.bundle.id)
        .subscribe((settings) => {
          // Remove '- Default Title' or 'Default Title' from productTitle
          if (settings.products && Array.isArray(settings.products)) {
            settings.products = settings.products.map((p) => {
              if (!p.productTitle) return p;
              // Remove any case/spacing/hyphen variation of 'default title'
              const cleanedTitle = p.productTitle
                .replace(/-?\s*default\s*title\s*/gi, "")
                .trim();
              return {
                ...p,
                productTitle: cleanedTitle,
              };
            });
          }
          this.bundleSettings = settings;

          // Set default frequency if available
          if (
            this.bundleSettings.frequencyOptions &&
            this.bundleSettings.frequencyOptions.length > 0
          ) {
            this.selectedFrequency =
              this.bundleSettings.frequencyOptions.find(
                (opt) =>
                  opt.frequency === this.order.frequency &&
                  opt.type === this.order.frequencyType,
              ) || null;
            this.bundleForm.patchValue({
              selectedFrequency: this.selectedFrequency,
            });
          }

          if (this.initialLoad) {
            const displayItems: QPilotScheduledOrderItem[] = [];
            if (this.bundle && this.bundle.siteId && this.bundle.id) {
              if ((this.order.scheduledOrderItems ?? []).length > 0) {
                (this.order.scheduledOrderItems ?? []).forEach((item) => {
                  displayItems.push(item);
                });
              }

              this.selectedProducts = displayItems.map((item) => ({
                productId: item.product?.productId || 0,
                externalProductId: (item.product?.id || 0).toString(),
                productTitle: item.product?.title || "",
                imageThumbUrl: item.product?.imageUrl || "",
                // Map availability string to enum value
                availability: (() => {
                  const availString = item.product?.availability?.toLowerCase();
                  switch (availString) {
                    case "outofstock":
                    case "out of stock":
                      return Availability.OutOfStock;
                    case "preorder":
                    case "pre order":
                      return Availability.PreOrder;
                    default: // Includes 'instock', null, undefined, and unknown values
                      return Availability.InStock;
                  }
                })(),
                price: item.product?.price || 0,
                salePrice: item.product?.salePrice,
                stock: item.product?.stock,
                quantity: item.quantity || 1, // Add quantity here
              }));

              this.initialLoad = false;

              this.calculateRewards();
            }
          }
        });
    }
  }

  ngOnDestroy(): void {
    if (this.productsSubscription) {
      this.productsSubscription.unsubscribe();
    }
    if (this.forkJoinSubscription) {
      this.forkJoinSubscription.unsubscribe();
    }
    if (this.getBundleByIdSubscription) {
      this.getBundleByIdSubscription.unsubscribe();
    }
  }

  goBack() {
    this.dialog.closeAll();
  }

  onRemove(ev: BundleProductDto) {
    this.onDecrement(ev);
  }
  onIncrement(productToAdd: BundleProductDto) {
    const existingProductIndex = this.selectedProducts.findIndex(
      (item) => item.productId === productToAdd.productId,
    );

    if (existingProductIndex > -1) {
      // Product already exists, increment quantity
      this.selectedProducts = this.selectedProducts.map((item, index) =>
        index === existingProductIndex
          ? { ...item, quantity: (item.quantity ?? 0) + 1 }
          : item,
      );
    } else {
      // Product doesn't exist, add it with quantity 1
      this.selectedProducts = [
        ...this.selectedProducts,
        { ...productToAdd, quantity: 1 },
      ];
    }
    this.calculateRewards();
  }

  onDecrement(productToRemove: BundleProductDto) {
    const existingProductIndex = this.selectedProducts.findIndex(
      (item) => item.productId === productToRemove.productId,
    );

    if (existingProductIndex > -1) {
      const product = this.selectedProducts[existingProductIndex];
      if (product.quantity && product.quantity > 1) {
        this.selectedProducts = this.selectedProducts.map((item, index) =>
          index === existingProductIndex
            ? { ...item, quantity: (item.quantity ?? 1) - 1 }
            : item,
        );
      } else {
        this.selectedProducts = [
          ...this.selectedProducts.slice(0, existingProductIndex),
          ...this.selectedProducts.slice(existingProductIndex + 1),
        ];
      }
      this.calculateRewards();
    }
  }

  onSubmit() {
    const selectedProducts = this.selectedProducts;
    if (selectedProducts.length === 0) {
      errorSnackBar(
        this.snackBar,
        "Please add at least one product to the bundle.",
      );
      this.dialog.closeAll();
      return;
    }

    const selectedFrequency = this.bundleForm.get("selectedFrequency")
      ?.value as BundleShippingFrequencyOptionsDto | null;

    if (!this.order || !this.order.scheduledOrderItems) {
      console.error("Order data is not available for comparison.");
      errorSnackBar(this.snackBar, "Failed: Order data missing.");
      return;
    }

    const aggregatedSelectedProducts = new Map<number, BundleProductDto>();
    selectedProducts.forEach((item) => {
      const existing = aggregatedSelectedProducts.get(item.productId);
      if (existing) {
        existing.quantity = (existing.quantity ?? 0) + (item.quantity ?? 1);
      } else {
        // Store a copy with quantity initialized correctly
        aggregatedSelectedProducts.set(item.productId, {
          ...item,
          quantity: item.quantity ?? 1,
        });
      }
    });

    // Create a map of original order items for easier lookup and modification
    const originalItemsMap = new Map(
      (this.order.scheduledOrderItems ?? []).map((item) => [
        Number(item.product?.productId),
        item,
      ]),
    );

    const finalOrderItems: QPilotScheduledOrderItem[] = [];
    const itemsToAdd: RemappedOmit<QPilotScheduledOrderItem, "id">[] = [];

    aggregatedSelectedProducts.forEach((selectedProduct, productId) => {
      const originalItem = originalItemsMap.get(productId);
      if (originalItem) {
        originalItem.quantity = selectedProduct.quantity;
        finalOrderItems.push(originalItem);
        originalItemsMap.delete(productId);
      } else {
        itemsToAdd.push({
          scheduledOrderId: this.order.id,
          productId: parseInt(selectedProduct.externalProductId),
          price: selectedProduct.price,
          salePrice: selectedProduct.salePrice,
          quantity: selectedProduct.quantity,
          metadata: {
            shopify: {
              _bundle_id: this.bundle.id.toString(),
            },
          },
          cycles: this.order.cycles, //
          product: (() => {
            const prod = this.allProducts?.find(
              (x) => x?.productId === selectedProduct.productId,
            );
            if (!prod) return undefined;
            return {
              ...prod,
            } as QPilotProduct;
          })(),
        });
      }
    });

    this.order.scheduledOrderItems = [
      ...finalOrderItems,
      ...(itemsToAdd as QPilotScheduledOrderItem[]),
    ];

    // Update order with selected frequency if available
    if (selectedFrequency) {
      this.order.frequency = selectedFrequency.frequency;
      this.order.frequencyType = selectedFrequency.type;
    }

    this.qPilotCloudApiService.updateScheduledOrder(this.order).subscribe({
      next: (results: QPilotScheduledOrder) => {
        successSnackBar(this.snackBar, "Bundle items updated successfully.");
        if (this.siteSettingsService.useNextime) {
          this.deliveryService.setShouldFetchNextOccurrenceDate(false);
        }
        this.dataService.updateOrder({
          ...results,
          bundleId: this.bundle.id,
          bundleName: this.bundle.name,
        });
        this.dialog.closeAll();
      },
      error: (err: unknown) => {
        console.error("An error occurred during the process", err);
        errorSnackBar(this.snackBar, "An unexpected error occurred.");
      },
    });
  }
}
